import { Archive, ArchiveRestore, Folder } from "lucide-react";
import { Link } from "react-router";
import { crewRoleLabel, SETUP_STEP_COUNT, type Project } from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { EmptyState } from "@/components/gb/empty-state";
import { Button } from "@/components/ui/button";
import { useArchiveProject, useUnarchiveProject } from "./api";
import { useDocuments } from "./documents-api";
import { useJurisdiction } from "./phases-api";
import { ProjectOverview } from "./project-overview";

/**
 * The Project tab. Three states, and all three are real screens rather than a
 * spinner standing in for two of them:
 *
 *   setup-incomplete  "Resume project setup · Step N of 4"
 *   archived          a retired site: read-only framing plus Unarchive
 *   active            the overview
 *
 * The "no project" state that used to live here has moved to the picker, which
 * is now the only place it can happen — and the loading and error states have
 * moved to `ProjectLayout`, which resolves the id for every screen at once.
 */
export function ProjectScreen() {
  const { project, projectId } = useProjectContext();

  if (project.status === "setup-incomplete") {
    return (
      <ContentColumn>
        <EmptyState
          icon={Folder}
          title={project.name}
          description="Setup isn’t finished yet. Pick up where you left off — everything you’ve entered so far is saved."
          action={
            <div className="flex flex-col items-center gap-2.5">
              <Button asChild className="hover:bg-brand-deep">
                <Link to={`/projects/${projectId}/setup/${project.setupStep}`}>
                  Resume project setup
                </Link>
              </Button>
              <span className="font-display text-[0.72rem] font-semibold uppercase tracking-[0.09em] text-muted">
                Step {project.setupStep} of {SETUP_STEP_COUNT}
              </span>
            </div>
          }
        />
      </ContentColumn>
    );
  }

  if (project.status === "archived") {
    return (
      <ContentColumn>
        <EmptyState
          icon={Archive}
          title={project.name}
          description="This site is archived. It stays out of the way in the picker, and everything filed against it is kept."
          action={<UnarchiveButton projectId={projectId} />}
        />
      </ContentColumn>
    );
  }

  return (
    <ContentColumn>
      <ProjectOverviewWithProfile project={project} />
      <ArchivePanel projectId={projectId} name={project.name} />
    </ContentColumn>
  );
}

/**
 * Archive lives here rather than on the picker row.
 *
 * The picker's rows are `<button>`s (that is what makes them keyboard-operable),
 * and a button inside a button is invalid HTML. Putting both verbs on the
 * project's own page also means the destructive-looking control sits inside the
 * thing it affects, which is where a user expects to find it.
 */
function ArchivePanel({
  projectId,
  name,
}: {
  projectId: string;
  name: string;
}) {
  const archive = useArchiveProject();

  return (
    <section className="mt-6 rounded-card-lg border-[1.5px] border-border bg-surface p-5">
      <h2 className="font-display text-[0.8rem] font-bold uppercase tracking-[0.06em] text-ink">
        Archive this site
      </h2>
      <p className="mt-1.5 max-w-[62ch] text-[0.88rem] leading-[1.5] text-ink-prose">
        Archiving moves {name} out of the default project list. Nothing is
        deleted, and you can restore it at any time.
      </p>
      {archive.error && (
        <p
          role="alert"
          className="mt-3 text-[0.85rem] font-semibold text-danger"
        >
          {archive.error.message}
        </p>
      )}
      <Button
        variant="secondary"
        size="sm"
        className="mt-4"
        disabled={archive.isPending}
        onClick={() => archive.mutate(projectId)}
      >
        <Archive size={15} />
        {archive.isPending ? "Archiving…" : "Archive project"}
      </Button>
    </section>
  );
}

function UnarchiveButton({ projectId }: { projectId: string }) {
  const unarchive = useUnarchiveProject();
  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        className="hover:bg-brand-deep"
        disabled={unarchive.isPending}
        onClick={() => unarchive.mutate(projectId)}
      >
        <ArchiveRestore size={16} />
        {unarchive.isPending ? "Restoring…" : "Unarchive project"}
      </Button>
      {unarchive.error && (
        <p role="alert" className="text-[0.85rem] font-semibold text-danger">
          {unarchive.error.message}
        </p>
      )}
    </div>
  );
}

/**
 * The overview, with the country's role → document chips and the document count.
 *
 * Both queries live here rather than inside `ProjectOverview` so that component
 * stays a pure render of what it is handed, and is testable without a query
 * client. Neither branches on `isPending` — a missing count renders the
 * Documents signpost without its trailing counter, which is the whole content
 * of that section's loading state.
 */
function ProjectOverviewWithProfile({ project }: { project: Project }) {
  const jurisdiction = useJurisdiction(project.country ?? undefined);
  const documents = useDocuments(project.id);

  const roleProfile = jurisdiction.data
    ? Object.entries(jurisdiction.data.roleProfiles).map(([role, docs]) => ({
        role: crewRoleLabel(role),
        docs: docs.map((doc) => jurisdiction.data!.docLabels[doc] ?? doc),
      }))
    : undefined;

  return (
    <ProjectOverview
      project={project}
      roleProfile={roleProfile}
      documentCount={documents.data?.length}
    />
  );
}
